import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classnames from "classnames";
import { useHistory, Switch, Route, useLocation } from "react-router-dom";
import { Filter } from "./components/Filter";
import { SearchInput } from "./components/SearchInput";
import { FeaturedSlider } from "../../components/FeaturedSlider";
import { toggleWatchLaterAPI } from "./api/toggleWatchLater";
import {
  getFilterFromSearchParams,
  preferredOrder,
  setFilterToSearchParams,
  sortByDate,
  sortByString,
  isEmptyFilter,
  sortByFreemium,
} from "./utils/utils";
import { CardsContext } from "./CardsContext";
import { AppContext } from "../../AppContext";
import { SkeletonLoader } from "./components/SkeletonLoader/SkeletonLoader";
import { SearchPage } from "./pages/SearchPage";
import { WatchLaterPage } from "./pages/WatchLaterPage";
import { BrowsePage } from "./pages/BrowsePage";
import { HomePage } from "./pages/HomePage";
import { FilterPage } from "./pages/FilterPage";
import { ClassPage } from "./pages/ClassPage";
import { CoursePage } from "./pages/CoursePage";
import { ContinueWatchingPage } from "./pages/ContinueWatchingPage";
import { PremiumBanner } from "./components/PremiumBanner";
import { PopupCardSeries } from "../../components/PopupCardSeries";
import { defaultFilter } from "./constants";
import { capitalizeFirstLetter, sendPageViewEvent } from "../../utils/utils";
import { isCardContinueWatching } from "../../utils/cards";
import { useDebouncedEffect } from "./utils/utils";
import "./Cards.scss";
import "./styles/video.scss";
import "./styles/dark.scss";

export function Cards() {
  const history = useHistory();
  const location = useLocation();
  const {
    target,
    isTargetClasses,
    isTargetCourses,
    cardsConfig,
    cards,
    setCards,
    updateCardsInfo,
    routes,
    isLegacy,
    isLoading,
  } = useContext(CardsContext);
  const { user, coaches } = useContext(AppContext);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [search, setSearch] = useState(null);
  const [browseLegacyCards] = useState([]);

  const isTargertChallenges = target === "challenges";

  const coachesFilter = useMemo(() => {
    const map = new Map();

    if (!cards || !coaches) {
      return map;
    }

    cards.forEach((card) => {
      card.coach?.forEach((coachId) => {
        map.set(coachId, coaches.get(coachId));
      });
    });

    return map;
  }, [cards, coaches]);

  const filterItems = useMemo(() => {
    if (!cards || !coachesFilter || !cardsConfig.filter_sort) {
      return {};
    }

    const accessSet = new Set();
    const levelSet = new Set();
    const typeSet = new Set();
    const subjectsSet = new Set();

    cards.forEach((classItem) => {
      classItem.access && accessSet.add(classItem.access);

      classItem.meta_level?.forEach((level) => {
        levelSet.add(level);
      });

      classItem.meta_type?.forEach((type) => {
        typeSet.add(type);
      });

      isTargetClasses &&
        classItem.tags?.forEach((tag) => {
          subjectsSet.add(tag.name);
        });
    });

    return {
      access: [...accessSet].sort(
        preferredOrder(cardsConfig.filter_sort.access)
      ),
      type: [...typeSet].sort(preferredOrder(cardsConfig.filter_sort.type)),
      level: [...levelSet].sort(preferredOrder(cardsConfig.filter_sort.level)),
      coach: [...coachesFilter.values()].sort((a, b) =>
        sortByString(a.title, b.title)
      ),
      sortBy: ["Upload date", "Coach", "Watched"],
      ...(isTargetClasses
        ? { subjects: [...subjectsSet].sort((a, b) => sortByString(a, b)) }
        : {}),
    };
  }, [cards, isTargetClasses, cardsConfig, coachesFilter]);

  const [filter, setFilter] = useState(() => {
    if (location.pathname !== routes.filter || !location.search) {
      return defaultFilter;
    }

    return getFilterFromSearchParams(location.search);
  });

  const [filterTitle, setFilterTitle] = useState("Search Results");
  const [currentCardSeries, setCurrentCardSeries] = useState(null);
  const [seriesSeason, setSeriesSeason] = useState(1);
  const [isShowPopupCardSeries, setIsShowPopupCardSeries] = useState(false);
  const isPageInit = useRef(false);

  const seeAllBrowse = useCallback(
    ({ title, slug, filter }, redirect = true) => {
      redirect && history.push(`${routes.browse}${slug}/`);
      setFilterTitle(title);
      if (filter) {
        setFilter({
          ...defaultFilter,
          access: filter.access || [],
          type: filter.type || [],
          level: filter.level || [],
          coach: filter.coach || [],
          hasSeries: filter?.hasSeries || false,
          ...(isTargetClasses ? { subjects: filter.tags || [] } : {}),
        });
      }
    },
    [routes, history, isTargetClasses]
  );

  const resetFilter = useCallback(() => {
    setFilter(defaultFilter);
    setFilterTitle("Search Results");
    history.push(routes.filter);
    sendPageViewEvent("Filter");
  }, [routes, history]);

  const toggleWatchLaterView = useCallback(() => {
    const isWatchLaterPage = location.pathname === routes.watchLater;

    setSearch("");
    setFilter(defaultFilter);

    if (isWatchLaterPage) {
      history.push(routes.home);
      sendPageViewEvent(`Poker Coaching ${target} | PokerCoaching.com`);
    } else {
      history.push(routes.watchLater);
    }
  }, [routes, history, location.pathname, target]);

  const seeContinueWatching = useCallback(() => {
    history.push(routes.continueWatching);
  }, [routes, history]);

  const handleSeriesRoute = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const name = searchParams.get("series");
    const seriesSeason = searchParams.get("season") || 1;

    if (!name) {
      return;
    }

    const card = cards.find((cardItem) => cardItem.post_name.includes(name));

    if (!card) {
      return;
    }

    setCurrentCardSeries(card);
    setSeriesSeason(seriesSeason);
    setIsShowPopupCardSeries(true);
  }, [location, cards]);

  useEffect(() => {
    if (isPageInit.current || isLoading) {
      return;
    }

    handleSeriesRoute();
    isPageInit.current = true;
  }, [isLoading, handleSeriesRoute]);

  const browseCards = useMemo(() => {
    if (!cards || !cardsConfig.browse) {
      return;
    }

    const cardsWithoutSeries = [];
    const cardsWithSeries = [];

    cards.forEach((card, i) => {

      // Add legacy cards to browse.
      if (card.access === 'Free' && card.tags && card.tags.some(tag => tag.slug.includes('legacy-'))) {
        card.tags.forEach(tag => {
          if(!tag.slug.includes('legacy-')) {return;}
          let browseLegacyCard = browseLegacyCards.find(browseLegacyCard => browseLegacyCard.slug === tag.slug);
          if (!browseLegacyCard) {
            browseLegacyCard = {
              title: tag.name,
              slug: tag.slug,
              data: [],
              filter: {
                access: ['Free'],
                type: [tag.name]
              }
            };
            browseLegacyCards.push(browseLegacyCard);
          }
          if (!browseLegacyCard.data.some(existingCard => existingCard.id === card.id)) {
            browseLegacyCard.data.push(card);
          }
        });
      }

      if (card.hide_from_listing) return;

      if (card?.series?.length) {
        cardsWithSeries.push(card);
        return;
      }

      cardsWithoutSeries.push(card);
    });

    const cardsToShow = [...cardsWithSeries, ...cardsWithoutSeries]

    const freeCardsResult =
      user.type !== "pcp"
        ? cardsToShow.filter((card) => {
            return (
              card.access === "Free" ||
              (isTargetCourses ? card.freemiumCountdown : false)
            );
          })
        : [];

    const watchLaterCardsResult =
      isTargetClasses && user.type !== "free" && user.type !== "guest"
        ? cardsToShow.filter((card) => card.watch_later)
        : [];

    const continueWatchingCardsResult = isTargetClasses
      ? cardsToShow.filter(
          (card) =>
            isCardContinueWatching(card) ||
            card.series.some((seria) => isCardContinueWatching(seria))
        )
      : [];

    const trendingNowCardsResult = isTargetClasses
      ? cardsToShow.filter(
          (card) =>
            card.trendAllTime ||
            card.series.some((seria) => seria.trendAllTime) ||
            card.freemiumCountdown
        )
      : null;

    const otherBrowseCardsResult = cardsConfig.browse.map((browseItem) => ({
      ...browseItem,
      data: cardsWithoutSeries
        .filter((classItem) => {
          const isAccessPassed = browseItem.filter.access
            ? browseItem.filter.access.some(
                (accessItem) => classItem.access === accessItem
              )
            : true;
          const isTypePassed = browseItem.filter.type
            ? classItem.meta_type?.some((typeItem) =>
                browseItem.filter.type.includes(typeItem)
              )
            : true;
          const isLevelPassed = browseItem.filter.level
            ? classItem.meta_level?.some((levelItem) =>
                browseItem.filter.level.includes(levelItem)
              )
            : true;
          const isTagsPassed = browseItem.filter.tags
            ? classItem.tags?.some((tagItem) =>
                browseItem.filter.tags.includes(tagItem.name)
              )
            : true;
          const isCoachPassed = browseItem.filter.coach
            ? classItem.coach.some((coachItem) =>
                browseItem.filter.coach.includes(coachItem)
              )
            : true;

          return (
            isAccessPassed &&
            isTypePassed &&
            isLevelPassed &&
            isTagsPassed &&
            isCoachPassed
          );
        })
        .sort((a) => sortByFreemium(a)),
    }));

    return [
      {
        title: `Free ${capitalizeFirstLetter(target)}`,
        slug: "free",
        filter: {
          access: ["Free"],
        },
        data: freeCardsResult
          .sort((a, b) => sortByDate(a.date, b.date))
          .sort((a) => sortByFreemium(a)),
      },
      ...(cardsWithSeries.length
        ? [
            {
              title: "Multi-Part Series",
              slug: "series",
              filter: {
                hasSeries: true,
              },
              data: cardsWithSeries.sort((a, b) => sortByDate(a.date, b.date)),
            },
          ]
        : []),
      ...(isTargetClasses
        ? [
            {
              title: "Trending Now",
              slug: "trending-now",
              filter: null,
              data: trendingNowCardsResult.sort((a) => sortByFreemium(a)),
              counter: false,
              limit: null,
            },
          ]
        : []),
      ...(isTargetClasses
        ? [
            {
              title: "Watch List",
              slug: null,
              filter: null,
              data: watchLaterCardsResult.sort((a) => sortByFreemium(a)),
              onSeeAll: toggleWatchLaterView,
            },
          ]
        : []),
      ...(isTargetClasses
        ? [
            {
              title: "New Classes",
              slug: "new-classes",
              filter: {},
              data: cardsWithoutSeries
                .sort((a, b) => sortByDate(a.date, b.date))
                .sort((a) => sortByFreemium(a)),
            },
          ]
        : []),
      ...(isTargetClasses
        ? [
            {
              title: "Continue Watching",
              slug: "continue-watching",
              filter: null,
              data: continueWatchingCardsResult.sort((a) => sortByFreemium(a)),
              onSeeAll: seeContinueWatching,
            },
          ]
        : []),
      ...otherBrowseCardsResult,
    ];
  }, [
    user,
    cardsConfig,
    target,
    cards,
    toggleWatchLaterView,
    seeContinueWatching,
    isTargetClasses,
    isTargetCourses,
  ]);

  const performSearch = useCallback(
    (search) => {
      if (typeof search === "object" && !search) return;

      const query = search.trim();

      if (query) {
        history.push(`${routes.search}?query=${encodeURIComponent(query)}`);
        setFilter(defaultFilter);
      } else {
        setSearch(null);
        if (location.pathname.includes(routes.watchLater)) return;
        history.push(routes.home);
      }
    },
    [history, routes, location]
  );

  useDebouncedEffect(
    () => {
      performSearch(search);

      //actid=51
      !!search && window.analytics?.track("search:query", {
          "query": search,
          "parent_object_title": target.charAt(0).toUpperCase() + target.slice(1)
        });
    },

    [search],
    500
  );

  function handleChangeSearch(e) {
    const query = e.target.value;

    setSearch(query);
  }

  const changeFilter = useCallback(
    (filterValues) => {
      const searchParams = setFilterToSearchParams(filterValues);

      setFilter(filterValues);
      setSearch(null);
      setFilterTitle("Search Results");
      history.push(`${routes.filter}?${searchParams}`);
    },
    [history, routes]
  );

  const toggleWatchLater = useCallback(
    async (toggleCard) => {
      function toggleWatchLater(cards) {
        return cards.map((card) =>
          card.id === toggleCard.id
            ? {
                ...card,
                watch_later: !card.watch_later,
              }
            : card
        );
      }

      try {
        setCards(toggleWatchLater);

        const apiResult = await toggleWatchLaterAPI(toggleCard.id);

        if (!apiResult?.success) {
          throw new Error("Not added to watch later");
        }
      } catch (e) {
        console.error(e);
        setCards(toggleWatchLater);
      }
    },
    [setCards]
  );

  const backToHome = useCallback(() => {
    setFilter(defaultFilter);
    history.push(routes.home);
    sendPageViewEvent(`Poker Coaching ${target} | PokerCoaching.com`);
  }, [routes, history, target]);

  const backFromCardPage = useCallback(async () => {
    if (search) {
      performSearch(search);
      return;
    }

    history.push(routes.home);
    await updateCardsInfo();

    if (isEmptyFilter(filter, isTargetClasses)) {
      history.push(routes.home);
      document.title = `${capitalizeFirstLetter(target)} – PokerCoaching.com`;
      sendPageViewEvent(`Poker Coaching ${target} | PokerCoaching.com`);
    } else {
      changeFilter(filter);
    }
  }, [
    search,
    performSearch,
    routes,
    filter,
    isTargetClasses,
    target,
    history,
    updateCardsInfo,
    changeFilter,
  ]);

  const classSeeAll = useCallback((coachId) => {
    setFilter({
      ...defaultFilter,
      coach: [coachId],
    });
  }, []);

  const handleCloseCardSeries = useCallback(() => {
    setCurrentCardSeries(null);
    setIsShowPopupCardSeries(false);
  }, []);

  return (
    <div className={classnames("Cards", `Cards_${target}`)}>
      <div
        className={classnames("Cards__head", {
          Cards__head_hidden: !isHeaderVisible,
        })}
      >
        <h1 className="Cards__title">{isLegacy ? 'Legacy ' : ''}{cardsConfig.title}</h1>

        {!isLoading && !isLegacy && (
          <>
            <SearchInput value={search} onChange={handleChangeSearch} />

            <div className="Cards__filter">
              <Filter
                filterDisabled={
                  location.pathname === routes.search ||
                  location.pathname === routes.watchLater
                }
                filter={filter}
                filterItems={filterItems}
                onChange={changeFilter}
                onReset={resetFilter}
                onWatchLater={toggleWatchLaterView}
              />
            </div>
          </>
        )}
      </div>

      {isLoading ? (
        <div className="Cards__body">
          <SkeletonLoader />
        </div>
      ) : (
        <>
          { isLegacy ? (
            <>
              <div className="Cards__body">
                <Switch>

                  <Route path={`${routes.browse}:id/`}>
                    <BrowsePage
                      browseCards={browseLegacyCards}
                      filter={filter}
                      filterTitle={filterTitle}
                      onToggleWatchLater={toggleWatchLater}
                      onBack={backToHome}
                      onSeeAll={seeAllBrowse}
                    />
                  </Route>


                  <Route path={routes.home}>
                    <>
                      <HomePage
                        browseCards={browseLegacyCards}
                        onToggleWatchLater={toggleWatchLater}
                        onSeeAll={seeAllBrowse}
                      />
                    </>
                  </Route>
                </Switch>
              </div>
            </>
          ) : (
            <>
              <div className="Cards__body">
                <Switch>
                  <Route path={routes.search}>
                    <SearchPage onToggleWatchLater={toggleWatchLater}/>
                  </Route>

                  {user.type !== "guest" && (
                    <Route path={routes.watchLater}>
                      <WatchLaterPage onToggleWatchLater={toggleWatchLater}/>
                    </Route>
                  )}

                  <Route path={routes.continueWatching}>
                    <ContinueWatchingPage
                      onToggleWatchLater={toggleWatchLater}
                      onBack={backToHome}
                    />
                  </Route>

                  <Route path={routes.filter}>
                    <FilterPage
                      filter={filter}
                      filterTitle={filterTitle}
                      onBack={backToHome}
                      onToggleWatchLater={toggleWatchLater}
                    />
                  </Route>

                  <Route path={`${routes.browse}:id/`}>
                    <BrowsePage
                      browseCards={browseCards}
                      filter={filter}
                      filterTitle={filterTitle}
                      onToggleWatchLater={toggleWatchLater}
                      onBack={backToHome}
                      onSeeAll={seeAllBrowse}
                    />
                  </Route>

                  {isTargetClasses && (
                    <Route path={routes.card}>
                      <ClassPage
                        onMount={() => setIsHeaderVisible(false)}
                        onUnmount={() => setIsHeaderVisible(true)}
                        onBack={backFromCardPage}
                        onSeeAll={classSeeAll}
                      />
                    </Route>
                  )}

                  {isTargetCourses && (
                    <Route path={routes.card}>
                      <CoursePage
                        onMount={() => setIsHeaderVisible(false)}
                        onUnmount={() => setIsHeaderVisible(true)}
                        onBack={backFromCardPage}
                      />
                    </Route>
                  )}

                  {isTargertChallenges && (
                    <Route path={routes.home}>
                      <BrowsePage
                        browseCards={browseCards}
                        filter={filter}
                        filterTitle={filterTitle}
                        onToggleWatchLater={toggleWatchLater}
                        onBack={backToHome}
                        onSeeAll={seeAllBrowse}
                      />
                    </Route>
                  )}

                  <Route path={routes.home}>
                    <>
                      <FeaturedSlider slides={cardsConfig.featured_slider}/>

                      <HomePage
                        browseCards={browseCards}
                        onToggleWatchLater={toggleWatchLater}
                        onSeeAll={seeAllBrowse}
                      />
                    </>
                  </Route>
                </Switch>
              </div>

              {cardsConfig?.banners?.footer_premium_show && <PremiumBanner />}

              {isShowPopupCardSeries && (
                <PopupCardSeries
                  searchParam
                  card={currentCardSeries}
                  seriesSeason={seriesSeason}
                  closeModal={handleCloseCardSeries}
                />
              )}
            </>
          )}

        </>
      )}
    </div>
  );
}
